import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { ParcelListShipment } from '../../../models/parcelDeliveryMiles';
import {
  ListRow,
  TrackingNumberText,
  CellContainer,
  LastCell,
  MoreDropdownCell,
} from './parcel-list-row.styles';
import Tag from '../../../common/tag/tag.component';
import { FontSizes } from '../../../common/typography/typography.styles';
import { TimeLabel } from '../../parcel-creation/pudo-list-item/pudo-list-item.styles';
import { IParcelListPathValue } from '../../../routes/parcel-list/config';
import TruncatedTextWithTooltip from '../../../common/truncated-text-with-tooltip/truncated-text-with-tooltip.component';
import { TrackingNumber } from '../parcel-list-card/parcel-list-card.styles';
import MoreDropdown from '../more-dropdown/more-dropdown.component';
import { useParcelItem } from '../hooks/use-parcel-item.hook';
import DetailsSidebar from '../details-sidebar/details-sidebar.component';
import PaymentButton from '../payment-button/payment-button.component';
import PrintLabelButton from '../print-label-button/print-label-button.component';
import { useStore } from '../../../stores/store';

import PinnedLabelComponent from '../pinned-label/pinned-label.component';

interface Props {
  parcel: ParcelListShipment;
  routeName: IParcelListPathValue['path'];
  onToggleFavorite: (value: boolean) => void;
  onToggleArchive: (value: boolean) => void;
  onCopy: () => void;
}

const ParcelListRow = ({
  parcel,
  routeName,
  onToggleFavorite,
  onToggleArchive,
  onCopy,
}: Props) => {
  const {
    parcelListStore: { isLoadingShipmentApi },
  } = useStore();

  const { tracking_number: trackingNumber, is_favorite: isFavorite } = parcel;
  const {
    actions,
    statusConfig,
    senderAddress,
    recipientAddress,
    senderName,
    recipientName,
    createdDate,
    deliveryDate,
    itemDescriptions,
    sidebarName,
    isLoadingParcel,
    totalPrice,
    footerKind,
    handleClick,
    handleKeyDown,
  } = useParcelItem({
    parcel,
    routeName,
    onToggleFavorite,
    onToggleArchive,
    onCopy,
  });

  const { t } = useTranslation();

  const isNoPayButton = footerKind !== 'pay' && footerKind !== 'label';
  const moreDropdownRef = useRef<HTMLDivElement | null>(null);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!moreDropdownRef.current) return;

    const observer = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) {
          setCount((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );
    observer.observe(moreDropdownRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const footer = (() => {
    // eslint-disable-next-line default-case
    switch (footerKind) {
      case 'pay':
        return (
          <PaymentButton
            contentStyle='thin'
            size='mini'
            parcel={parcel}
          />
        );
      case 'label':
        return (
          <PrintLabelButton
            shipmentId={parcel.id}
            isRow
          />
        );
      case 'status_paid':
        return (
          <TruncatedTextWithTooltip
            textType='label'
            fontSize={FontSizes.small}>
            {`${t('paid')} ${totalPrice}`}
          </TruncatedTextWithTooltip>
        );
      case 'status_to_pay':
        return (
          <TruncatedTextWithTooltip
            textType='label'
            fontSize={FontSizes.small}>
            {`${t('parcel_card_total_to_pay')} ${totalPrice}`}
          </TruncatedTextWithTooltip>
        );
    }
  })();

  return (
    <>
      <ListRow
        $hasPayButton={!isNoPayButton}
        tabIndex={0}
        onKeyDown={(event) => handleKeyDown(event, parcel.id)}
        onClick={() => handleClick(parcel.id)}>
        <CellContainer
          $isLoading={isLoadingShipmentApi}
          $width='16rem'>
          <TrackingNumber $noMargin>
            <TrackingNumberText>{trackingNumber}</TrackingNumberText>
            {isFavorite && <PinnedLabelComponent onlyIcon />}
          </TrackingNumber>
        </CellContainer>

        <CellContainer
          $isLoading={isLoadingShipmentApi}
          $width='12rem'>
          <TimeLabel>{createdDate}</TimeLabel>
        </CellContainer>

        <CellContainer
          $isLoading={isLoadingShipmentApi}
          $width='12rem'>
          <TimeLabel>{deliveryDate}</TimeLabel>
        </CellContainer>

        <CellContainer
          $isLoading={isLoadingShipmentApi}
          $width='16rem'>
          <Tag
            size='small'
            type={statusConfig.type || 'semi-filled'}
            color={statusConfig.color}>
            {t(statusConfig.text)}
          </Tag>
        </CellContainer>
        <CellContainer
          $isLoading={isLoadingShipmentApi}
          $width='24rem'>
          <TruncatedTextWithTooltip
            textType='label'
            fontSize={FontSizes.small}>
            {itemDescriptions}
          </TruncatedTextWithTooltip>
        </CellContainer>
        <CellContainer
          $isLoading={isLoadingShipmentApi}
          $width='16rem'>
          <TruncatedTextWithTooltip
            textType='label'
            fontSize={FontSizes.small}>
            {senderAddress}
          </TruncatedTextWithTooltip>
        </CellContainer>
        <CellContainer
          $isLoading={isLoadingShipmentApi}
          $width='16rem'>
          <TruncatedTextWithTooltip
            textType='label'
            fontSize={FontSizes.small}>
            {recipientAddress}
          </TruncatedTextWithTooltip>
        </CellContainer>
        <CellContainer
          $isLoading={isLoadingShipmentApi}
          $width='16rem'>
          <TruncatedTextWithTooltip
            textType='label'
            fontSize={FontSizes.small}>
            {senderName}
          </TruncatedTextWithTooltip>
        </CellContainer>
        <CellContainer
          $isLoading={isLoadingShipmentApi}
          $width='16rem'>
          <TruncatedTextWithTooltip
            textType='label'
            fontSize={FontSizes.small}>
            {recipientName}
          </TruncatedTextWithTooltip>
        </CellContainer>
        <LastCell
          $isLoading={isLoadingShipmentApi}
          $width='17.2rem'>
          {footer}
        </LastCell>
        <MoreDropdownCell
          ref={moreDropdownRef}
          $width='4rem'>
          <MoreDropdown
            strategy='fixed'
            isInPortal={false}
            key={`${count}-${parcel.id}`}
            items={actions}
          />
        </MoreDropdownCell>
      </ListRow>
      <DetailsSidebar
        name={sidebarName}
        createdDate={createdDate}
        deliveryDate={deliveryDate}
        menuItems={actions}
        isLoadingParcel={isLoadingParcel}
      />
    </>
  );
};

export default ParcelListRow;
